import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import { printPatientDispense } from './printPatientDispense';

/** الرقم الوطني الليبي 12 رقماً — نفس PatientIdentifierRule في الخادم. الأرقام العربية تُحوَّل للاتينية. */
function normalizeIdentifier(raw) {
    return String(raw || '')
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
        .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
        .replace(/[^0-9A-Za-z]/g, '')
        .toUpperCase();
}

export default {
    name: 'PatientDispenses',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewPatientDispensesUi()) {
            this.logout();
            return;
        }
        this.loadDepartments();
        this.GetInfo();
    },

    computed: {
        /**
         * أول دواء من «قائمة الأدوية التخصصية» في الفاتورة (قرار إدارة الصيدلة 2026-10-07) —
         * وجوده يجعل رقم الهوية إلزامياً: وطني، أو جواز/إقامة للأجانب.
         */
        specializedLineName() {
            const ids = new Set((this.specializedProductIds || []).map(Number));
            const line = (this.form.lines || []).find((l) => l.productsId != null && ids.has(Number(l.productsId)));
            if (!line) return '';
            const p = this.productOf(line.productsId);
            return (p && p.name) || 'دواء تخصصي';
        },
        identityRequired() {
            return !!this.specializedLineName;
        },
        canDispense() {
            return this.canDispenseToPatientsUi();
        },
        /** يحوّل تنبيه المراجعة من نص ثابت إلى فحص: أحمر حين يوجد منتهٍ في رصيد صنف مختار. */
        confirmHasExpired() {
            return (this.confirmLines || []).some((x) => !!x.expiredText);
        },
        /** القسم المختار في نافذة الصرف — يحدد الأصناف المعروضة. */
        selectedDepartment() {
            return (this.departments || []).find(d => Number(d.id) === Number(this.form.facilityDepartmentId)) || null;
        },
        selectedDepartmentName() {
            return this.selectedDepartment ? this.selectedDepartment.name : '';
        },
        /** لا يمكن الصرف من قسم بلا مخزن: لم يُصرف له أي صنف بعد. */
        selectedDepartmentHasStock() {
            return !!(this.selectedDepartment && this.selectedDepartment.hasStocking);
        },
        statsCards() {
            const s = this.statistics || {};
            return [
                this.statCard('إجمالي الفواتير', s.total || 0, 'primary', 'ti ti-file-invoice'),
                this.statCard('فواتير منفذة', s.executed || 0, 'success', 'ti ti-circle-check'),
                this.statCard('فواتير ملغاة', s.cancelled || 0, 'danger', 'ti ti-ban'),
                this.statCard('عدد المرضى', s.patients || 0, 'info', 'ti ti-users'),
            ];
        }
    },

    data() {
        return {
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            Search: '',
            statusFilter: 0,
            departmentFilter: 0,
            Info: [],
            statistics: {},

            departments: [],
            products: [],
            // أصناف القسم المعلَّمة «تخصصي (قائمة الإدارة)» — تفرض رقم هوية المريض.
            specializedProductIds: [],
            loadingProducts: false,

            createDialog: false,
            // نافذة مراجعة بيانات الأصناف قبل إتمام الصرف (إلزامية)
            confirmDialog: false,
            confirmLines: [],
            confirming: false,
            patientLookupDone: false,
            patientIsNew: false,
            lastDispenseText: '',
            /** آخر رقم بُحث عنه — يمنع تكرار الاستدعاء عند كل خروج من الحقل. */
            lastLookedUp: '',
            form: {
                facilityDepartmentId: null,
                nationalNo: '',
                identifierKind: 1,
                patientName: '',
                patientPhone: '',
                note: '',
                lines: []
            },

            detailDialog: false,
            detail: null,

            cancelDialog: false,
            cancelForm: { id: 0, code: '', reason: '' }
        };
    },

    methods: {
        /* ── قوائم ─────────────────────────────────────────────── */

        loadDepartments() {
            this.$http.GetPatientDispenseDepartments()
                .then((response) => {
                    this.departments = (response.data && response.data.info) || [];
                })
                .catch(() => {
                    this.departments = [];
                    this.$helper.ShowMessage('error', 'خطأ', 'تعذر تحميل أقسام المرفق. حدّث الصفحة وأعد المحاولة.');
                });
        },

        GetInfo(pageNo) {
            this.pageNo = pageNo || this.pageNo || 1;
            this.$blockUI.Start();
            this.$http.GetPatientDispenses(this.pageNo, this.pageSize, this.Search, this.departmentFilter, this.statusFilter)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.Info = response.data.info || [];
                    this.pages = response.data.count || 0;
                    this.statistics = response.data.statistics || {};
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.Info = [];
                    this.pages = 0;
                });
        },

        /* ── نافذة الصرف ───────────────────────────────────────── */

        openCreateDialog() {
            const onlyDepartment = this.departments.length === 1 ? this.departments[0].id : null;
            this.form = {
                facilityDepartmentId: onlyDepartment,
                nationalNo: '',
                identifierKind: 1,
                patientName: '',
                patientPhone: '',
                note: '',
                lines: []
            };
            this.products = [];
            this.patientLookupDone = false;
            this.patientIsNew = false;
            this.lastDispenseText = '';
            this.lastLookedUp = '';
            if (!this.departments.length) this.loadDepartments();
            // الأرصدة تتغيّر بصرف زميل أو بإلغاء فاتورة، فنجلبها عند كل فتح لا مرة واحدة.
            if (this.form.facilityDepartmentId) this.onDepartmentChanged();
            this.addLine();
            this.createDialog = true;
        },

        /** تحديث الأرصدة دون المساس بالبنود التي أدخلها المستخدم. */
        /** يرجّع وعداً: نافذة المراجعة تنتظره حتى تعرض أرصدة اللحظة لا أرصدة الفتح. */
        refreshProducts() {
            if (!this.form.facilityDepartmentId) return Promise.resolve();
            this.loadingProducts = true;
            return this.$http.GetPatientDispenseProducts(this.form.facilityDepartmentId, '')
                .then((response) => {
                    this.loadingProducts = false;
                    this.products = (response.data && response.data.info) || [];
                    this.specializedProductIds = (response.data && response.data.specializedProductIds) || [];
                })
                .catch(() => {
                    this.loadingProducts = false;
                });
        },

        onDepartmentChanged() {
            this.products = [];
            // تغيير القسم يُبطل الأصناف المختارة لأنها من رصيد قسم آخر.
            this.form.lines.forEach((l) => { l.productsId = null; l.unitMeasureId = null; });
            if (!this.form.facilityDepartmentId) return;
            this.loadingProducts = true;
            this.$http.GetPatientDispenseProducts(this.form.facilityDepartmentId, '')
                .then((response) => {
                    this.loadingProducts = false;
                    this.products = (response.data && response.data.info) || [];
                    this.specializedProductIds = (response.data && response.data.specializedProductIds) || [];
                })
                .catch(() => {
                    this.loadingProducts = false;
                    this.products = [];
                    this.specializedProductIds = [];
                });
        },

        /** تعديل الرقم يبطل نتيجة البحث السابقة حتى لا يبقى اسم مريض آخر معروضاً. */
        onIdentifierInput() {
            if (String(this.form.nationalNo || '').trim() !== this.lastLookedUp) {
                this.patientLookupDone = false;
                this.lastDispenseText = '';
            }
        },

        /**
         * بحث المريض برقم الهوية — يملأ الاسم إن كان مسجلاً من قبل.
         * يُستدعى تلقائياً عند الخروج من الحقل، فلا يحتاج المستخدم لضغط زر.
         * @param {boolean} manual طلب صريح من المستخدم (زر أو Enter) — يتجاوز منع التكرار ويُظهر التنبيهات.
         */
        lookupPatient(manual) {
            const raw = String(this.form.nationalNo || '').trim();
            if (!raw) {
                // الخروج من حقل فارغ أمر طبيعي — لا نزعج المستخدم برسالة إلا إن طلب البحث صراحةً.
                if (manual) this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل رقم هوية المريض أولاً.');
                return;
            }
            if (!manual && raw === this.lastLookedUp) return;
            this.lastLookedUp = raw;
            this.$blockUI.Start();
            this.$http.FindPatientByNationalNo(raw)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.patientLookupDone = true;
                    if (response.data && response.data.found) {
                        const p = response.data.info;
                        this.patientIsNew = false;
                        this.form.patientName = p.name;
                        this.form.patientPhone = p.phone || '';
                        this.form.identifierKind = p.identifierKind || 1;
                        this.lastDispenseText = p.lastDispenseCode
                            ? `آخر صرف: ${p.lastDispenseCode} بتاريخ ${this.momentTime(p.lastDispenseOn)}`
                            : 'لا يوجد صرف سابق لهذا المريض في مرفقكم.';
                    } else {
                        this.patientIsNew = true;
                        this.form.patientName = '';
                        this.form.patientPhone = '';
                        this.lastDispenseText = 'مريض جديد — أدخل الاسم وسيُسجَّل تلقائياً عند الصرف.';
                    }
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('error', 'خطأ', 'تعذر البحث عن المريض.');
                });
        },

        /** بلا رقم ← «بلا رقم» (0) أياً كان النوع المختار — الرقم اختياري لغير التخصصية. */
        effectiveIdentifierKind() {
            const kind = Number(this.form.identifierKind);
            if (kind === 0 || !normalizeIdentifier(this.form.nationalNo)) return 0;
            return kind || 1;
        },

        /** نفس قاعدة الخادم (PatientIdentifierRule) — تنبيه قبل الإرسال. */
        identityProblem() {
            const no = normalizeIdentifier(this.form.nationalNo);
            const kind = Number(this.form.identifierKind);
            if (this.identityRequired) {
                const drug = this.specializedLineName;
                if (![1, 2, 4].includes(kind))
                    return `«${drug}» من قائمة الأدوية التخصصية — يلزم رقم هوية المريض: رقم وطني، أو جواز سفر / إقامة للأجانب.`;
                if (!no)
                    return `«${drug}» من قائمة الأدوية التخصصية — أدخل رقم هوية المريض.`;
            }
            if (no && kind === 1 && !/^[0-9]{12}$/.test(no))
                return 'الرقم الوطني 12 رقماً — تحقق منه.';
            return null;
        },

        addLine() {
            this.form.lines.push({ productsId: null, quantity: 1, unitMeasureId: null, note: '' });
        },

        removeLine(index) {
            this.form.lines.splice(index, 1);
        },

        productOf(productsId) {
            return (this.products || []).find(p => Number(p.id) === Number(productsId)) || null;
        },

        unitsOf(productsId) {
            const p = this.productOf(productsId);
            return p ? (p.units || []) : [];
        },

        /**
         * المتاح للصرف لا الإجمالي: المنتهي محسوب في الرصيد لكنه لا يُصرف.
         * ويُرجع للإجمالي إن لم يُرسل الخادم الحقل (نسخة أقدم) فلا تنكسر الشاشة.
         */
        dispensableOf(p) {
            if (!p) return 0;
            return p.dispensableQuantity !== undefined && p.dispensableQuantity !== null
                ? Number(p.dispensableQuantity)
                : Number(p.availableQuantity || 0);
        },

        dispensableTextOf(p) {
            if (!p) return '';
            return p.dispensableText || p.availableText || '';
        },

        /** نص يُظهر المنتهي صراحةً — «٢٠ علبة (ويوجد ٥ منتهية لا تُصرف)». */
        availabilitySummary(p) {
            if (!p) return '';
            const base = this.dispensableTextOf(p);
            return p.expiredQuantity > 0
                ? `${base} (ويوجد ${p.expiredText} منتهية الصلاحية لا تُصرف)`
                : base;
        },

        availableTextOf(productsId) {
            return this.availabilitySummary(this.productOf(productsId));
        },

        hasExpiredStock(productsId) {
            const p = this.productOf(productsId);
            return !!(p && p.expiredQuantity > 0);
        },

        /** عند اختيار الصنف نضبط الوحدة على الوحدة الأساسية حتى لا تبقى فارغة. */
        onProductChanged(line) {
            const units = this.unitsOf(line.productsId);
            const base = units.find(u => u.isBaseUnit);
            line.unitMeasureId = base ? base.unitMeasureId : (units.length ? units[0].unitMeasureId : null);
        },

        /** معاينة الكمية بالوحدة الأساسية قبل الإرسال — تطابق ما سيُخصم فعلياً. */
        baseQuantityPreview(line) {
            const units = this.unitsOf(line.productsId);
            const unit = units.find(u => Number(u.unitMeasureId) === Number(line.unitMeasureId));
            const factor = unit ? Number(unit.baseUnitsPerUnit || 1) : 1;
            const qty = Number(line.quantity || 0);
            if (!qty || qty <= 0) return '';
            const total = qty * factor;
            const p = this.productOf(line.productsId);
            const baseName = p ? (p.baseUnitName || '') : '';
            return factor === 1 ? '' : `= ${total} ${baseName}`;
        },

        /** الكمية بالوحدة الأساسية: هي ما يُخصم فعلاً من الرصيد والدفعات. */
        baseQuantityOf(line) {
            const units = this.unitsOf(line.productsId);
            const unit = units.find(u => Number(u.unitMeasureId) === Number(line.unitMeasureId));
            const factor = unit ? Number(unit.baseUnitsPerUnit || 1) : 1;
            return Number(line.quantity || 0) * factor;
        },

        /**
         * توزيع الكمية على الدفعات بترتيب الخادم نفسه: الأقرب انتهاءً أولاً (FEFO).
         * عرض فقط — لكنه مبني على أرصدة أُعيد جلبها لحظة فتح المراجعة، فما يُعرض
         * هو ما يُخصم. لو عُرض غير ذلك لأعطت النافذة طمأنينة كاذبة في صرف دواء.
         */
        batchPlanOf(line) {
            const product = this.productOf(line.productsId);
            const batches = (product && product.batches) || [];
            if (!batches.length) return [];

            let remaining = this.baseQuantityOf(line);
            const baseName = (product && product.baseUnitName) || '';

            // المنتهي لا يُخصم منه — نفس قاعدة الخادم (ExpiryPolicy). ولو بقيت المحاكاة
            // على الترتيب القديم لعرضت الخصم من التشغيلة المنتهية والخادم يخصم من غيرها.
            return batches.map((b) => {
                const available = b.isExpired ? 0 : Number(b.quantity || 0);
                const take = remaining > 0 ? Math.min(remaining, available) : 0;
                remaining -= take;
                return {
                    batchNumber: b.batchNumber,
                    expiryDate: b.expiryDate,
                    quantityText: b.quantityText,
                    isExpired: !!b.isExpired,
                    take,
                    takeText: take > 0 ? (baseName ? take + ' ' + baseName : String(take)) : '',
                };
            });
        },

        /** دفعة منتهية أو على وشك — تُميَّز في المراجعة ليراها الصيدلي قبل أن يصرف. */
        batchExpiryClass(batch) {
            if (!batch || !batch.expiryDate) return '';
            const days = (new Date(batch.expiryDate) - new Date()) / 86400000;
            if (days < 0) return 'text-danger fw-semibold';
            if (days <= 90) return 'text-warning fw-semibold';
            return '';
        },

        batchExpiryNote(batch) {
            if (!batch || !batch.expiryDate) return 'بلا تاريخ صلاحية';
            const days = Math.floor((new Date(batch.expiryDate) - new Date()) / 86400000);
            if (days < 0) return 'منتهية الصلاحية';
            if (days <= 90) return `تنتهي خلال ${days} يوم`;
            return '';
        },

        exceedsAvailable(line) {
            const p = this.productOf(line.productsId);
            if (!p) return false;
            const units = this.unitsOf(line.productsId);
            const unit = units.find(u => Number(u.unitMeasureId) === Number(line.unitMeasureId));
            const factor = unit ? Number(unit.baseUnitsPerUnit || 1) : 1;
            return (Number(line.quantity || 0) * factor) > this.dispensableOf(p);
        },

        /**
         * بوابة المراجعة الإلزامية قبل الصرف: تتحقق، ثم تُعيد جلب الأرصدة، ثم تُعيد
         * التحقق على الأرصدة الجديدة، ثم تفتح نافذة بيانات الأصناف. الصرف لا يقع هنا.
         */
        submitCreate() {
            if (!this.form.facilityDepartmentId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر القسم من القائمة المنسدلة.');
                return;
            }
            if (!this.selectedDepartmentHasStock) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'هذا القسم لا يملك رصيداً بعد — اصرف له الأصناف من مخزن المرفق أولاً.');
                return;
            }
            if (!String(this.form.patientName || '').trim()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل اسم المريض.');
                return;
            }
            const identityProblem = this.identityProblem();
            if (identityProblem) {
                this.$helper.ShowMessage('warning', 'تنبيه', identityProblem);
                return;
            }

            const lines = (this.form.lines || [])
                .filter(x => x.productsId !== null && x.productsId !== undefined && Number(x.quantity) > 0)
                .map(x => ({
                    productsId: Number(x.productsId),
                    quantity: Number(x.quantity),
                    unitMeasureId: x.unitMeasureId === null || x.unitMeasureId === undefined ? null : Number(x.unitMeasureId),
                    note: x.note || null
                }));

            if (!lines.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أضف صنفاً واحداً على الأقل بكمية أكبر من صفر.');
                return;
            }

            const over = (this.form.lines || []).find(l => l.productsId && this.exceedsAvailable(l));
            if (over) {
                const p = this.productOf(over.productsId);
                this.$helper.ShowMessage('warning', 'تنبيه', `الكمية المطلوبة للصنف ${p ? p.name : ''} أكبر من المتاح للصرف: ${this.availabilitySummary(p)}.`);
                return;
            }

            // أرصدة اللحظة لا أرصدة فتح النافذة: زميل في نفس القسم قد يكون صرف
            // بين الفتح والتأكيد، فتصير الدفعة المعروضة غير الدفعة المخصومة.
            this.$blockUI.Start();
            this.refreshProducts().then(() => {
                this.$blockUI.Stop();

                const missing = (this.form.lines || []).find(l => l.productsId && !this.productOf(l.productsId));
                if (missing) {
                    this.$helper.ShowMessage('warning', 'تغيّر الرصيد', 'أحد الأصناف لم يعد في رصيد القسم — راجع الأسطر.');
                    return;
                }

                const overNow = (this.form.lines || []).find(l => l.productsId && this.exceedsAvailable(l));
                if (overNow) {
                    const p = this.productOf(overNow.productsId);
                    this.$helper.ShowMessage('warning', 'تغيّر الرصيد',
                        `الكمية المطلوبة للصنف ${p ? p.name : ''} أكبر من المتاح للصرف الآن: ${this.availabilitySummary(p)}.`);
                    return;
                }

                this.confirmLines = (this.form.lines || [])
                    .filter(x => x.productsId && Number(x.quantity) > 0)
                    .map((line) => {
                        const product = this.productOf(line.productsId) || {};
                        const units = this.unitsOf(line.productsId);
                        const unit = units.find(u => Number(u.unitMeasureId) === Number(line.unitMeasureId));
                        return {
                            name: product.name,
                            scientificName: product.scientificName,
                            nationalCode: product.nationalCode,
                            dosageForm: product.dosageForm,
                            manufacturerName: product.manufacturerName,
                            availableText: this.availabilitySummary(product),
                            expiredText: product.expiredQuantity > 0 ? product.expiredText : null,
                            oldestExpiredBatch: product.oldestExpiredBatch,
                            oldestExpiredDate: product.oldestExpiredDate,
                            requestedText: `${line.quantity} ${unit ? unit.unitName : ''}`.trim(),
                            baseQuantity: this.baseQuantityOf(line),
                            baseUnitName: product.baseUnitName,
                            note: line.note,
                            batches: this.batchPlanOf(line).filter(b => b.take > 0),
                        };
                    });

                this.confirmDialog = true;
            });
        },

        /** التنفيذ الفعلي — لا يُستدعى إلا من نافذة المراجعة. */
        confirmDispense() {
            const lines = (this.form.lines || [])
                .filter(x => x.productsId !== null && x.productsId !== undefined && Number(x.quantity) > 0)
                .map(x => ({
                    productsId: Number(x.productsId),
                    quantity: Number(x.quantity),
                    unitMeasureId: x.unitMeasureId === null || x.unitMeasureId === undefined ? null : Number(x.unitMeasureId),
                    note: x.note || null
                }));

            const body = {
                facilityDepartmentId: Number(this.form.facilityDepartmentId),
                nationalNo: normalizeIdentifier(this.form.nationalNo) || null,
                identifierKind: this.effectiveIdentifierKind(),
                patientName: String(this.form.patientName).trim(),
                patientPhone: this.form.patientPhone || null,
                note: this.form.note || null,
                lines
            };

            this.confirming = true;
            this.$blockUI.Start();
            this.$http.CreatePatientDispense(body)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.confirming = false;
                    this.confirmDialog = false;
                    this.createDialog = false;
                    this.$helper.ShowMessage('success', 'تم الصرف', (response.data && response.data.message) || 'تم الصرف.');
                    this.GetInfo(1);
                    if (response.data && response.data.id) this.openDetail({ id: response.data.id });
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.confirming = false;
                    this.confirmDialog = false;
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                    // الرفض غالباً بسبب رصيد تغيّر بعد فتح النافذة — نُحدّث المعروض
                    // فيرى المستخدم الرصيد الحقيقي بدل أن يعيد المحاولة على رقم قديم.
                    this.refreshProducts();
                });
        },

        /* ── التفاصيل والطباعة والإلغاء ────────────────────────── */

        openDetail(item) {
            this.$blockUI.Start();
            this.$http.GetPatientDispense(item.id)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.detail = response.data.info;
                    this.detailDialog = true;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('error', 'خطأ', 'تعذر فتح الفاتورة.');
                });
        },

        printDetail() {
            if (!this.detail) return;
            printPatientDispense(this.detail);
        },

        openCancelDialog() {
            if (!this.detail) return;
            this.cancelForm = { id: this.detail.id, code: this.detail.code, reason: '' };
            this.cancelDialog = true;
        },

        submitCancel() {
            if (!String(this.cancelForm.reason || '').trim()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل سبب الإلغاء.');
                return;
            }
            this.$blockUI.Start();
            this.$http.CancelPatientDispense(this.cancelForm.id, { reason: this.cancelForm.reason.trim() })
                .then((response) => {
                    this.$blockUI.Stop();
                    this.cancelDialog = false;
                    this.detailDialog = false;
                    this.$helper.ShowMessage('success', 'تم', (response.data && response.data.message) || 'تم الإلغاء.');
                    this.GetInfo();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        statusClass(status) {
            return Number(status) === 9 ? 'bg-label-danger' : 'bg-label-success';
        }
    }
};
